import type { BoardState } from '@/types';
import { shareViaSDK } from '@/sdk/funway';

// ── Types ──

export interface ShareCardData {
  title: string;         // "Resonance #142 ★★★"
  stats: string;         // "⚡ 4/6 strikes | 🔄 0 resets"
  harmony: string;       // "🎵 Perfect harmony in 2:34"
  board: string;         // emoji grid representation
  url: string;           // "resonance.funway.boutique"
}

// ── Helpers ──

/**
 * Extracts a numeric puzzle number from an ID like "ch3-05" → 35.
 * Chapter contributes (chapter - 1) * 10 + puzzle index.
 */
export function extractPuzzleNumber(puzzleId: string): number {
  const match = puzzleId.match(/^ch(\d+)-(\d+)$/);
  if (!match) return 0;
  const chapter = parseInt(match[1] ?? '0', 10);
  const index = parseInt(match[2] ?? '0', 10);
  return (chapter - 1) * 10 + index;
}

/** Renders stars as filled ★ and empty ☆ characters (out of 3). */
export function renderStarString(stars: number): string {
  const clamped = Math.max(0, Math.min(3, stars));
  return '★'.repeat(clamped) + '☆'.repeat(3 - clamped);
}

/** Formats seconds as M:SS. */
export function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Maps a frequency (1–12) to a colored circle emoji. */
export function frequencyToEmoji(freq: number): string {
  if (freq <= 2) return '🔴';
  if (freq === 3) return '🟡';
  if (freq <= 5) return '🟢';
  if (freq <= 7) return '🔵';
  if (freq <= 9) return '🟣';
  if (freq <= 11) return '🟠';
  return '⚪'; // freq 12
}

// ── Main Functions ──

export function generateShareCard(
  puzzleId: string,
  _puzzleTitle: string,
  stars: number,
  strikesUsed: number,
  strikeBudget: number,
  resetsUsed: number,
  solveTimeSeconds: number,
  board: BoardState,
): ShareCardData {
  const puzzleNumber = extractPuzzleNumber(puzzleId);
  const starStr = renderStarString(stars);
  const title = `Resonance #${puzzleNumber} ${starStr}`;

  const stats = `⚡ ${strikesUsed}/${strikeBudget} strikes | 🔄 ${resetsUsed} resets`;

  const harmonyLabel = stars === 3 ? 'Perfect harmony' : 'Harmony achieved';
  const harmony = `🎵 ${harmonyLabel} in ${formatTime(solveTimeSeconds)}`;

  const boardEmojis = board.nodes.map((n) => frequencyToEmoji(n.frequency)).join(' ');

  return {
    title,
    stats,
    harmony,
    board: boardEmojis,
    url: 'resonance.funway.boutique',
  };
}

export function formatShareText(card: ShareCardData): string {
  return `${card.title}\n${card.stats}\n${card.harmony}\n\n${card.board}\n\n${card.url}`;
}

export async function shareResult(
  text: string,
  score?: number,
): Promise<'shared' | 'copied' | 'failed'> {
  // Try Funway SDK first
  const sdkShared = await shareViaSDK(text, score);
  if (sdkShared) return 'shared';

  // Try Web Share API
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ text });
      return 'shared';
    } catch {
      // User cancelled or API failed — fall through to clipboard
    }
  }

  // Clipboard fallback
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return 'copied';
    }
    return 'failed';
  } catch {
    return 'failed';
  }
}
